// Location: src/pages/plan-summary/components/InsightsCard.tsx

import { useTranslation } from 'react-i18next';

interface Insight {
  type: 'pattern' | 'achievement' | 'suggestion' | 'warning';
  text: string;
}

interface InsightsCardProps {
  insights: Insight[];
}

export default function InsightsCard({ insights }: InsightsCardProps) {
  const { t } = useTranslation();

  const getInsightStyle = (type: string) => {
    switch (type) {
      case 'pattern':
        return {
          icon: 'ri-focus-3-line',
          color: 'text-blue-400',
          bg: 'bg-blue-500/10',
          glow: 'shadow-[0_0_15px_rgba(59,130,246,0.3)]'
        };
      case 'achievement':
        return {
          icon: 'ri-vip-crown-line',
          color: 'text-yellow-400',
          bg: 'bg-yellow-500/10',
          glow: 'shadow-[0_0_15px_rgba(250,204,21,0.3)]'
        };
      case 'suggestion':
        return {
          icon: 'ri-lightbulb-flash-line',
          color: 'text-green-400',
          bg: 'bg-green-500/10',
          glow: 'shadow-[0_0_15px_rgba(34,197,94,0.3)]'
        };
      case 'warning':
        return {
          icon: 'ri-pulse-line',
          color: 'text-orange-400',
          bg: 'bg-orange-500/10',
          glow: 'shadow-[0_0_15px_rgba(249,115,22,0.3)]'
        };
      default:
        return {
          icon: 'ri-magic-line',
          color: 'text-purple-400',
          bg: 'bg-purple-500/10',
          glow: 'shadow-[0_0_15px_rgba(168,85,247,0.3)]'
        };
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 lg:p-8 backdrop-blur-sm flex flex-col h-full relative overflow-hidden">
      {/* Subtle background ambient light */}
      <div className="absolute -top-24 -left-24 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>

      <div className="relative z-10 mb-8">
        <h3 className="text-xl font-light text-white tracking-tight">{t('planSummary.insights.title')}</h3>
        <p className="text-xs text-gray-500 mt-1">{t('planSummary.insights.subtitle')}</p>
      </div>

      <div className="relative z-10 flex-grow">
        {insights && insights.length > 0 ? (
          <div className="space-y-8">
            {insights.map((insight, index) => {
              const style = getInsightStyle(insight.type);

              return (
                <div key={index} className="flex items-start gap-4 group">
                  <div className={`w-10 h-10 rounded-full ${style.bg} ${style.color} flex items-center justify-center shrink-0 mt-0.5 transition-all duration-300 group-hover:${style.glow}`}>
                    <i className={`${style.icon} text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-medium text-gray-500 mb-1.5">{insight.type}</div>
                    <div className="text-gray-300 text-sm font-light leading-relaxed">
                      {insight.text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 flex flex-col items-center justify-center h-full">
            <div className="w-16 h-16 rounded-full bg-white/5 text-gray-600 flex items-center justify-center mb-4">
              <i className="ri-brain-line text-3xl"></i>
            </div>
            <div className="text-gray-500 text-sm font-light">
              {t('planSummary.insights.empty')}
            </div>
          </div>
        )}
      </div>

      <div className="relative z-10 mt-10 pt-6 border-t border-white/5">
        <div className="text-[10px] text-gray-500 uppercase tracking-widest font-medium mb-4">{t('planSummary.insights.generalTips.title')}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500/50"></div>
            <span className="text-xs text-gray-400 font-light">{t('planSummary.insights.generalTips.consistency')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500/50"></div>
            <span className="text-xs text-gray-400 font-light">{t('planSummary.insights.generalTips.timing')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-500/50"></div>
            <span className="text-xs text-gray-400 font-light">{t('planSummary.insights.generalTips.improvements')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}